import { useState } from "react";
import { api } from "../lib/api";
import { SERVICES } from "../lib/content";
import { CheckCircle2, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

export default function BookPage() {
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    organization: "",
    service_interest: SERVICES[0].title,
    consultation_datetime: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        consultation_datetime: new Date(form.consultation_datetime).toISOString(),
      };
      const { data } = await api.post("/bookings", payload);
      if (data.checkout_url) {
        toast.success("Redirecting to secure checkout...");
        window.location.href = data.checkout_url;
      } else {
        toast.success("Booking received. We'll be in touch shortly.");
        window.location.href = `/booking/success?booking_id=${data.booking_id}`;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="bg-[var(--crest-cream)]">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-12">
        <div className="eyebrow text-[var(--crest-gold)]">— Book a Consultation</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight mt-6 leading-[1.02] max-w-4xl">
          Begin the engagement.
        </h1>
        <p className="text-lg text-[var(--crest-muted)] mt-6 max-w-2xl">
          Reserve your consultation. A non-refundable deposit secures your time and is applied toward your final invoice.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-32 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-2" data-testid="booking-form">
            <input className="crest-input md:col-span-2" placeholder="Full Name" required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="booking-name" />
            <input type="email" className="crest-input" placeholder="Email" required value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="booking-email" />
            <input className="crest-input" placeholder="Phone (E.164 format, e.g., +17575550123)" required value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="booking-phone" />
            <input className="crest-input md:col-span-2" placeholder="Organization / Title (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="booking-org" />

            <div className="md:col-span-2 mt-6">
              <div className="eyebrow text-[var(--crest-muted)] mb-2">Service of Interest</div>
              <select className="crest-input" value={form.service_interest} onChange={(e) => setForm({ ...form, service_interest: e.target.value })} data-testid="booking-service">
                {SERVICES.map((s) => <option key={s.id}>{s.title}</option>)}
              </select>
            </div>

            <div className="md:col-span-2 mt-6">
              <div className="eyebrow text-[var(--crest-muted)] mb-2">Preferred Date & Time</div>
              <input type="datetime-local" className="crest-input" required value={form.consultation_datetime} onChange={(e) => setForm({ ...form, consultation_datetime: e.target.value })} data-testid="booking-datetime" />
            </div>

            <textarea className="crest-input md:col-span-2 mt-6" placeholder="Tell us about your goals (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" />

            <button className="btn-primary md:col-span-2 mt-8" disabled={submitting} data-testid="booking-submit">
              {submitting ? "Securing your slot..." : "Continue to Deposit"}
            </button>
          </form>
        </div>

        <aside className="md:col-span-5">
          <div className="border border-black/10 bg-white p-8">
            <div className="eyebrow text-[var(--crest-gold)]">— The Process</div>
            <ol className="mt-6 space-y-5 text-sm">
              {[
                ["Reserve", "Choose your date and pay the deposit to secure your slot."],
                ["Intake", "Complete the brief client intake form before our meeting."],
                ["Consult", "Meet with our studio to align on goals, scope, and direction."],
                ["Proposal", "Receive a customized engagement — deposit applied to your final invoice."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="font-serif text-2xl text-[var(--crest-gold)] leading-none">0{i + 1}</span>
                  <div>
                    <div className="font-medium">{t}</div>
                    <div className="text-[var(--crest-muted)] mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="hairline my-8" />
            <div className="eyebrow text-[var(--crest-gold)]">— Policy</div>
            <ul className="text-sm text-[var(--crest-muted)] mt-4 space-y-2">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--crest-gold)" }} /> Deposits are non-refundable.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--crest-gold)" }} /> Applied toward final invoice.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--crest-gold)" }} /> Reschedules require 12h notice.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--crest-gold)" }} /> Cancellations require 24h notice.</li>
            </ul>
            <div className="hairline my-8" />
            <div className="flex items-center gap-3 text-sm">
              <CalIcon size={16} style={{ color: "var(--crest-gold)" }} />
              <span className="text-[var(--crest-muted)]">Confirmation emails + SMS reminders included.</span>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
